/*
 * Behaviour for sections/nfs-header.liquid — the First11-style header.
 * Everything is keyed off the [data-nfsh] element, so the theme editor can
 * re-render the section and this just re-initialises.
 *
 * Wat er in deze ronde bij is gekomen, en waarom:
 *   - de suggestielijst is nu een echte combobox. Het veld riep al
 *     aria-haspopup="listbox" en aria-autocomplete="list", maar er was geen
 *     listbox, geen options en geen aria-activedescendant: een schermlezer
 *     kreeg een lijst aangekondigd die niet bestond, en met het toetsenbord was
 *     er niets te kiezen. Pijltjes, Home, End, Enter en Escape werken nu.
 *   - het aantal treffers wordt via een aria-live gebied gemeld.
 *   - tijdens het zoeken blijft de vorige lijst staan met een dunne
 *     voortgangsbalk erboven. Eerder werd de lijst bij elke toetsaanslag
 *     vervangen door de tekst "Searching…", waardoor het paneel bij snel typen
 *     stond te knipperen.
 *   - een mislukt verzoek werd stil weggeslikt. Nu komt er een foutmelding met
 *     een knop om het opnieuw te proberen.
 *   - een leeg veld toont de laatste zoekopdrachten, zodat de bezoeker niet
 *     opnieuw hoeft te typen.
 *   - de lade houdt de focus vast zolang hij open is en geeft hem daarna terug
 *     aan de knop waarmee hij geopend werd.
 */
(() => {
  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const money = (value) => {
    const amount = parseFloat(value);
    if (Number.isNaN(amount)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  };

  /* suggest.json hands back the original upload, which is often 2000px+ wide.
     The CDN resizes on the width parameter, so ask it for a thumbnail. */
  const sized = (url, width) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  };

  const shuffle = (list) => {
    const copy = list.slice();
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const swap = copy[i];
      copy[i] = copy[j];
      copy[j] = swap;
    }
    return copy;
  };

  /* ---------- recente zoekopdrachten ----------
     Alleen wat de bezoeker zelf typte, alleen in zijn eigen browser, en met een
     knop om het te wissen. Elke toegang staat in een try: in een private
     venster gooit localStorage. */
  const RECENT_KEY = 'nfs:recent-searches';
  const RECENT_MAX = 6;

  const readRecent = () => {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string' && item.trim()) : [];
    } catch (error) {
      return [];
    }
  };

  const writeRecent = (list) => {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (error) {
      /* Opslag geweigerd of vol: de zoekfunctie werkt zonder geschiedenis prima. */
    }
  };

  const rememberSearch = (term) => {
    const value = String(term || '').trim();
    if (value.length < 2) return;
    const list = readRecent().filter((item) => item.toLowerCase() !== value.toLowerCase());
    list.unshift(value);
    writeRecent(list);
  };

  const forgetSearches = () => {
    try {
      window.localStorage.removeItem(RECENT_KEY);
    } catch (error) {
      /* niets te doen */
    }
  };

  /* Kept in step with --nfsh-slide in nfs-header.css. */
  const SLIDE_MS = 380;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* ---------- typed placeholder ----------
     Types a phrase out, holds it, wipes it letter by letter and moves to the
     next. Timings match le7sorelle.it, which this was modelled on: 100ms per
     character typed, 60ms per character deleted, 2400ms on a finished phrase
     and 500ms on an empty field.

     The lead phrase stays first; everything after it is reshuffled on every
     page load, so two visits in a row do not show the same run of shirts.

     This returns a controller instead of running on its own, because a field
     that is folded away behind a magnifier must not animate while nobody can
     see it — by the time the visitor opens it they would drop into the middle
     of a wipe. The toggle starts it on open and stops it on close, so it always
     begins on an empty field.

     It keeps running while the field has focus but no text: stopping on focus
     would kill it on phones, where opening the field focuses it straight away.
     Once there is text the placeholder is invisible anyway, so that is where it
     pauses.

     On reduced motion it drops the per-character animation and swaps whole
     phrases on a slow timer, unless the section overrides that. Worth knowing:
     iOS reports reduced motion both for the accessibility setting and for Low
     Power Mode, so this branch catches far more phones than you would guess. */
  const TYPE_SPEED = 100;
  const DELETE_SPEED = 60;
  const HOLD_TYPED = 2400;
  const HOLD_EMPTY = 500;
  const SWAP_EVERY = 4000;

  const createTyper = (field, input, force) => {
    if (!field || input.length < 2) return { start() {}, stop() {} };

    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    const phrases = input.length > 2 ? [input[0]].concat(shuffle(input.slice(1))) : input.slice();

    let timer = null;
    let running = false;
    let index = 0;
    /* Bumped on every stop so a run that is parked on an await resolves into a
       stale token and unwinds instead of writing over a newer run. */
    let token = 0;

    const wait = (ms) =>
      new Promise((resolve) => {
        timer = window.setTimeout(resolve, ms);
      });

    const stop = () => {
      if (!running) return;
      running = false;
      token += 1;
      window.clearTimeout(timer);
      field.placeholder = phrases[0];
    };

    const start = () => {
      if (running) return;
      running = true;
      token += 1;

      const mine = token;
      const live = () => running && mine === token;
      const reduced = still.matches && !force;

      (async () => {
        if (reduced) {
          while (live()) {
            field.placeholder = phrases[index];
            await wait(SWAP_EVERY);
            if (!live()) return;
            index = (index + 1) % phrases.length;
          }
          return;
        }

        field.placeholder = '';
        await wait(HOLD_EMPTY);

        while (live()) {
          const text = phrases[index];

          for (let i = 1; i <= text.length; i += 1) {
            if (!live()) return;
            field.placeholder = text.slice(0, i);
            await wait(TYPE_SPEED);
          }

          if (!live()) return;
          await wait(HOLD_TYPED);

          let rest = field.placeholder;
          while (rest.length > 0) {
            if (!live()) return;
            rest = rest.slice(0, -1);
            field.placeholder = rest;
            await wait(DELETE_SPEED);
          }

          if (!live()) return;
          await wait(HOLD_EMPTY);
          /* Carried over, not reset — so reopening the field picks up the next
             shirt rather than replaying the same one every time. */
          index = (index + 1) % phrases.length;
        }
      })();
    };

    field.addEventListener('input', stop);

    still.addEventListener('change', () => {
      if (!running) return;
      stop();
      start();
    });

    return { start, stop };
  };

  const init = (root) => {
    if (root.dataset.nfshBound === 'true') return;
    root.dataset.nfshBound = 'true';

    const spacer = document.getElementById(root.id + '-spacer');
    const drawer = root.querySelector('[data-nfsh-drawer]');
    const mobileSearch = root.querySelector('[data-nfsh-mobile-search]');
    const ignoreReducedMotion = root.hasAttribute('data-nfsh-ignore-reduced-motion');

    /* ---------- header height variables ----------
       Horizon's inline script in theme.liquid only measures <header-component>,
       which this header replaces. Other sections (sticky add to cart, drawers)
       read --header-height, so keep publishing it. */
    const publishHeight = () => {
      const height = root.classList.contains('is-stuck') && spacer ? spacer.offsetHeight : root.offsetHeight;
      const group = document.getElementById('header-group');
      let groupHeight = height;

      if (group) {
        groupHeight = 0;
        for (const child of group.children) {
          if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
        }
      }

      document.body.style.setProperty('--header-height', height + 'px');
      document.body.style.setProperty('--header-group-height', groupHeight + 'px');
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    };

    /* ---------- sticky ----------
       The attribute carries the mode: always, desktop or mobile. It is absent
       when the header should never stick. 990px is the same breakpoint the
       stylesheet uses to switch between the two layouts. */
    const stickyMode = root.getAttribute('data-nfsh-sticky');

    if (stickyMode && spacer) {
      const desktop = window.matchMedia('(min-width: 990px)');
      const stickyHere = () =>
        stickyMode === 'always' ||
        (stickyMode === 'desktop' && desktop.matches) ||
        (stickyMode === 'mobile' && !desktop.matches);

      let anchorTop = 0;
      let stuck = false;

      const unstick = () => {
        if (!stuck) return;
        stuck = false;
        root.classList.remove('is-stuck');
        spacer.classList.remove('is-active');
        publishHeight();
      };

      const measure = () => {
        if (stuck) return;
        anchorTop = root.getBoundingClientRect().top + window.scrollY;
        spacer.style.height = root.offsetHeight + 'px';
      };

      const onScroll = () => {
        if (!stickyHere()) {
          unstick();
          return;
        }
        const shouldStick = window.scrollY > anchorTop;
        if (shouldStick === stuck) return;
        stuck = shouldStick;
        spacer.style.height = root.offsetHeight + 'px';
        root.classList.toggle('is-stuck', stuck);
        spacer.classList.toggle('is-active', stuck);
        publishHeight();
      };

      const reset = () => {
        unstick();
        measure();
        onScroll();
        publishHeight();
      };

      measure();
      onScroll();

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', reset);
      desktop.addEventListener('change', reset);
    }

    publishHeight();
    window.addEventListener('load', publishHeight);

    /* ---------- drawer ----------
       De lade is een dialoog: zolang hij open is blijft de focus erin, en bij
       sluiten gaat hij terug naar de knop waarmee hij geopend werd. Zonder dat
       liep de tabvolgorde door de pagina eronder, die op dat moment onbereikbaar
       en onzichtbaar is. */
    let drawerOpener = null;

    if (drawer) {
      if (!drawer.hasAttribute('role')) drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
    }

    const setDrawer = (open) => {
      if (!drawer) return;
      const wasOpen = drawer.classList.contains('is-open');
      if (open === wasOpen) return;

      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.documentElement.style.overflow = open ? 'hidden' : '';
      root.querySelectorAll('[data-nfsh-open-drawer]').forEach((button) => {
        button.setAttribute('aria-expanded', String(open));
      });

      if (open) {
        const panel = drawer.querySelector('.nfsh-drawer__panel') || drawer;
        const first = panel.querySelector(FOCUSABLE);
        if (first) first.focus({ preventScroll: true });
      } else if (drawerOpener && document.contains(drawerOpener)) {
        drawerOpener.focus({ preventScroll: true });
        drawerOpener = null;
      }
    };

    root.querySelectorAll('[data-nfsh-open-drawer]').forEach((button) => {
      button.addEventListener('click', () => {
        drawerOpener = button;
        setDrawer(true);
      });
    });

    root.querySelectorAll('[data-nfsh-close-drawer]').forEach((button) => {
      button.addEventListener('click', () => setDrawer(false));
    });

    if (drawer) {
      drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;

        const panel = drawer.querySelector('.nfsh-drawer__panel') || drawer;
        const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null || el === document.activeElement
        );
        if (!items.length) return;

        const first = items[0];
        const last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    /* ---------- mobile search slide ----------
       Only exists in toggle mode, and the whole strip is display:none above
       990px, so everything here is inherently phone-only. */
    const searchToggle = root.querySelector('[data-nfsh-toggle-mobile-search]');

    if (searchToggle && mobileSearch) {
      /* The typer is attached to the wrapper further down in this function.
         Both handlers below only ever run on a click, long after that. */
      const mobileTyper = () => {
        const wrap = mobileSearch.querySelector('[data-nfsh-search]');
        return wrap ? wrap.nfshTyper : null;
      };

      let settleTimer = null;

      const closeMobileSearch = () => {
        if (!mobileSearch.classList.contains('is-open')) return;
        window.clearTimeout(settleTimer);
        /* Dropped first: the box has to clip again before the field starts
           travelling up, or it slides out over the logo row. */
        mobileSearch.classList.remove('is-settled');
        mobileSearch.classList.remove('is-open');
        searchToggle.setAttribute('aria-expanded', 'false');

        const field = mobileSearch.querySelector('input[type="search"]');
        if (field) field.blur();

        const panel = mobileSearch.querySelector('[data-nfsh-results]');
        if (panel) panel.setAttribute('hidden', '');

        const typer = mobileTyper();
        if (typer) typer.stop();

        publishHeight();
      };

      searchToggle.addEventListener('click', () => {
        const open = !mobileSearch.classList.contains('is-open');

        if (!open) {
          closeMobileSearch();
          return;
        }

        mobileSearch.classList.add('is-open');
        searchToggle.setAttribute('aria-expanded', 'true');

        const field = mobileSearch.querySelector('input[type="search"]');
        if (field) field.focus();

        const typer = mobileTyper();
        if (typer) typer.start();

        /* Only once the slide has finished may the box stop clipping, so the
           suggestions panel can hang below it. */
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          if (mobileSearch.classList.contains('is-open')) mobileSearch.classList.add('is-settled');
        }, SLIDE_MS);

        publishHeight();
      });

      /* Scrolling down folds the field away again. Only on the way down, and
         never while the field has focus: iOS scrolls the page by itself to make
         room for the soft keyboard, and that scroll would otherwise fold the
         search away the instant the visitor opened it. */
      let lastY = window.scrollY;

      window.addEventListener(
        'scroll',
        () => {
          const y = window.scrollY;
          const goingDown = y > lastY + 4;
          lastY = y;
          if (!goingDown) return;

          const field = mobileSearch.querySelector('input[type="search"]');
          if (field && document.activeElement === field) return;

          closeMobileSearch();
        },
        { passive: true }
      );
    }

    /* ---------- country and language dropdowns ---------- */
    const closeAllLoc = (except) => {
      root.querySelectorAll('[data-nfsh-loc]').forEach((field) => {
        if (field === except) return;
        const menu = field.querySelector('[data-nfsh-loc-menu]');
        const toggle = field.querySelector('[data-nfsh-loc-toggle]');
        if (menu) menu.setAttribute('hidden', '');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    };

    root.querySelectorAll('[data-nfsh-loc]').forEach((field) => {
      const toggle = field.querySelector('[data-nfsh-loc-toggle]');
      const menu = field.querySelector('[data-nfsh-loc-menu]');
      const form = field.closest('form');
      const hiddenInput = form ? form.querySelector('[data-nfsh-loc-input]') : null;
      if (!toggle || !menu) return;

      toggle.addEventListener('click', () => {
        const open = menu.hasAttribute('hidden');
        closeAllLoc(field);
        if (open) {
          menu.removeAttribute('hidden');
          const active = menu.querySelector('.is-active');
          if (active) menu.scrollTop = Math.max(0, active.offsetTop - 120);
        } else {
          menu.setAttribute('hidden', '');
        }
        toggle.setAttribute('aria-expanded', String(open));
      });

      menu.querySelectorAll('[data-nfsh-loc-value]').forEach((option) => {
        option.addEventListener('click', () => {
          if (!hiddenInput || !form) return;
          hiddenInput.value = option.getAttribute('data-nfsh-loc-value');
          /* The theme editor sandboxes the preview and swallows this POST, so the
             picker looks like it reverts there. It works on the storefront. */
          form.submit();
        });
      });
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-nfsh-loc]')) closeAllLoc(null);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeAllLoc(null);
      setDrawer(false);
    });

    /* ---------- predictive search ---------- */
    let panelSeq = 0;

    root.querySelectorAll('[data-nfsh-search]').forEach((wrap) => {
      const form = wrap.querySelector('form');
      const field = wrap.querySelector('input[type="search"]');
      const clear = wrap.querySelector('[data-nfsh-search-clear]');
      const results = wrap.querySelector('[data-nfsh-results]');
      if (!form || !field || !results) return;

      panelSeq += 1;
      const listId = (root.id || 'nfsh') + '-listbox-' + panelSeq;

      results.id = listId;
      /* De listbox is het paneel zelf. Zet je hem op de scroller, dan valt de
         doorklik in de voet erbuiten en kan aria-activedescendant er niet naar
         wijzen. De scroller, de voet en de voortgangsbalk zijn daarom
         presentational; de secties erbinnen zijn groups met een eigen label. */
      results.setAttribute('role', 'listbox');
      results.setAttribute('aria-label', 'Search suggestions');
      field.setAttribute('role', 'combobox');
      field.setAttribute('aria-controls', listId);
      field.setAttribute('aria-expanded', 'false');

      /* Onzichtbaar meldgebied. Een schermlezer hoort hier hoeveel treffers er
         zijn; op het scherm doet het aantal er niet toe, want de lijst staat er. */
      const status = document.createElement('p');
      status.className = 'nfsh-visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      wrap.appendChild(status);

      let phrases = [];
      try {
        phrases = JSON.parse(wrap.getAttribute('data-nfsh-phrases') || '[]').filter(Boolean);
      } catch (error) {
        phrases = [];
      }

      const typer = createTyper(field, phrases, ignoreReducedMotion);
      wrap.nfshTyper = typer;

      /* A field folded away behind the magnifier waits for the toggle to start
         it. Anything already on screen starts now. */
      const folded = Boolean(searchToggle) && Boolean(mobileSearch) && mobileSearch.contains(field);
      if (!folded) typer.start();

      let controller = null;
      let timer = null;
      let lastTerm = '';
      let hasResults = false;
      let options = [];
      let activeIndex = -1;

      const bar = '<div class="nfsh-results__bar" aria-hidden="true"></div>';

      const setBusy = (busy) => {
        results.setAttribute('aria-busy', busy ? 'true' : 'false');
      };

      const hide = () => {
        results.setAttribute('hidden', '');
        field.setAttribute('aria-expanded', 'false');
        field.removeAttribute('aria-activedescendant');
        options = [];
        activeIndex = -1;
      };

      /* Alles wat aanklikbaar is in het paneel is een option, in de volgorde
         waarin het op het scherm staat. Zo lopen de pijltjes precies langs wat
         de bezoeker ziet, inclusief de doorklik onderaan. */
      const indexOptions = () => {
        options = Array.from(results.querySelectorAll('[role="option"]'));
        options.forEach((option, i) => {
          option.id = listId + '-opt-' + i;
          option.setAttribute('aria-selected', 'false');
        });
        activeIndex = -1;
        field.removeAttribute('aria-activedescendant');
      };

      const show = (html) => {
        results.innerHTML = bar + html;
        results.removeAttribute('hidden');
        field.setAttribute('aria-expanded', 'true');
        indexOptions();
      };

      const setActive = (next) => {
        if (!options.length) return;

        if (activeIndex > -1 && options[activeIndex]) {
          options[activeIndex].classList.remove('is-active');
          options[activeIndex].setAttribute('aria-selected', 'false');
        }

        activeIndex = next;

        if (activeIndex < 0) {
          field.removeAttribute('aria-activedescendant');
          return;
        }

        const option = options[activeIndex];
        option.classList.add('is-active');
        option.setAttribute('aria-selected', 'true');
        field.setAttribute('aria-activedescendant', option.id);
        /* block: 'nearest' scrollt alleen als het echt nodig is, zodat de lijst
           niet bij elke pijl verspringt. */
        option.scrollIntoView({ block: 'nearest' });
      };

      const announce = (message) => {
        status.textContent = message;
      };

      /* ---------- panelen ---------- */

      const recentPanel = () => {
        const recent = readRecent();
        if (!recent.length) return '';

        let html =
          '<div class="nfsh-results__scroll" role="presentation">' +
          '<div class="nfsh-results__section" role="group" aria-label="Recent searches">' +
          '<p class="nfsh-results__heading">Recent<button type="button" data-nfsh-forget>Clear</button></p>' +
          '<div class="nfsh-results__list">';

        recent.forEach((term) => {
          html +=
            '<a role="option" href="/search?q=' +
            encodeURIComponent(term) +
            '&type=product">' +
            '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6">' +
            '<circle cx="10" cy="10" r="7.2"></circle><path d="M10 6v4.2l2.8 1.7" stroke-linecap="round"></path></svg>' +
            escapeHtml(term) +
            '</a>';
        });

        return html + '</div></div></div>';
      };

      const skeletonPanel = () => {
        let html = '<div class="nfsh-results__scroll" role="presentation"><div class="nfsh-results__section" role="presentation">';
        html += '<p class="nfsh-results__heading">Searching…</p>';
        for (let i = 0; i < 3; i += 1) {
          html +=
            '<div class="nfsh-results__skeleton" aria-hidden="true">' +
            '<span class="nfsh-results__skeleton-thumb"></span>' +
            '<span><span class="nfsh-results__skeleton-line"></span>' +
            '<span class="nfsh-results__skeleton-line nfsh-results__skeleton-line--short"></span></span></div>';
        }
        return html + '</div></div>';
      };

      const showRecent = () => {
        const html = recentPanel();
        if (!html) {
          hide();
          return;
        }
        hasResults = false;
        setBusy(false);
        show(html);
      };

      const render = (data, term) => {
        const found = (data && data.resources && data.resources.results) || {};
        const products = found.products || [];
        const collections = found.collections || [];
        /* Query suggestions carry `text`, not `title` — reading the wrong key is
           what printed "undefined" in the panel. */
        const suggestions = (found.queries || []).filter((item) => item && (item.text || item.title));
        const allUrl = '/search?q=' + encodeURIComponent(term) + '&type=product';

        setBusy(false);

        if (!products.length && !collections.length && !suggestions.length) {
          hasResults = false;
          show(
            '<div class="nfsh-results__state"><b>Nothing found</b>' +
              '<span>No shirts match “' +
              escapeHtml(term) +
              '”. Try a club, a country, a player or a year.</span></div>' +
              '<div class="nfsh-results__footer" role="presentation"><a class="nfsh-results__all" role="option" href="' +
              allUrl +
              '">Search anyway<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8h11M9 4l4 4-4 4"></path></svg></a></div>'
          );
          announce('No results for ' + term);
          return;
        }

        let html = '<div class="nfsh-results__scroll" role="presentation">';

        if (suggestions.length) {
          html +=
            '<div class="nfsh-results__section" role="group" aria-label="Suggestions"><p class="nfsh-results__heading" aria-hidden="true">Suggestions</p><div class="nfsh-results__list" role="presentation">';
          suggestions.slice(0, 4).forEach((item) => {
            html +=
              '<a role="option" href="' +
              escapeHtml(item.url) +
              '"><svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="9" cy="9" r="6"></circle><path d="m17 17-3.6-3.6"></path></svg>' +
              escapeHtml(item.text || item.title) +
              '</a>';
          });
          html += '</div></div>';
        }

        if (products.length) {
          html +=
            '<div class="nfsh-results__section" role="group" aria-label="Products"><p class="nfsh-results__heading" aria-hidden="true">Products</p><div class="nfsh-results__products" role="presentation">';
          products.slice(0, 6).forEach((product) => {
            const source = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
            const image = sized(source, 200);
            html +=
              '<a class="nfsh-results__product" role="option" href="' + escapeHtml(product.url) + '">' +
              (image ? '<img src="' + escapeHtml(image) + '" alt="" loading="lazy" width="60" height="75">' : '<span></span>') +
              '<span><span class="nfsh-results__product-title">' + escapeHtml(product.title) + '</span>' +
              '<span class="nfsh-results__product-price">' + escapeHtml(money(product.price)) + '</span></span></a>';
          });
          html += '</div></div>';
        }

        if (collections.length) {
          html +=
            '<div class="nfsh-results__section" role="group" aria-label="Collections"><p class="nfsh-results__heading" aria-hidden="true">Collections</p><div class="nfsh-results__list" role="presentation">';
          collections.slice(0, 5).forEach((collection) => {
            html += '<a role="option" href="' + escapeHtml(collection.url) + '">' + escapeHtml(collection.title) + '</a>';
          });
          html += '</div></div>';
        }

        html +=
          '</div><div class="nfsh-results__footer" role="presentation"><a class="nfsh-results__all" role="option" href="' +
          allUrl +
          '">View all results<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8h11M9 4l4 4-4 4"></path></svg></a></div>';

        hasResults = true;
        show(html);

        const total = products.length + collections.length + suggestions.length;
        announce(total === 1 ? '1 suggestion' : total + ' suggestions');
      };

      const fail = (term) => {
        setBusy(false);
        hasResults = false;
        show(
          '<div class="nfsh-results__state"><b>Search unavailable</b>' +
            '<span>We could not reach the shirts just now. Check your connection and try again.</span>' +
            '<button type="button" class="nfsh-results__retry" data-nfsh-retry>Try again</button></div>'
        );
        announce('Search failed');
        const retry = results.querySelector('[data-nfsh-retry]');
        if (retry) {
          retry.addEventListener('click', () => {
            field.focus();
            lookup(term);
          });
        }
      };

      const lookup = (term) => {
        if (controller) controller.abort();
        controller = new AbortController();

        setBusy(true);
        /* Alleen een skeleton als er nog helemaal niets staat. Zijn er al
           resultaten, dan blijven die staan met de voortgangsbalk erboven, zodat
           de lijst niet leegspringt bij elke toetsaanslag. */
        if (!hasResults) show(skeletonPanel());

        const url =
          '/search/suggest.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product,collection,query' +
          '&resources[limit]=6' +
          '&resources[options][unavailable_products]=last' +
          '&resources[options][fields]=title,product_type,variants.title,vendor,tag';

        fetch(url, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
          .then((data) => {
            if (field.value.trim() !== term) return;
            render(data, term);
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') return;
            fail(term);
          });
      };

      field.addEventListener('input', () => {
        const term = field.value.trim();
        if (clear) clear.toggleAttribute('hidden', term.length === 0);
        clearTimeout(timer);

        if (term.length < 2) {
          lastTerm = '';
          hasResults = false;
          if (controller) controller.abort();
          showRecent();
          return;
        }

        if (term === lastTerm) return;
        lastTerm = term;

        setBusy(true);
        /* Staat er nog niets, laat dan meteen een skeleton zien in plaats van
           een leeg paneel; staan er al resultaten, dan blijven die staan. */
        if (!hasResults) show(skeletonPanel());
        timer = setTimeout(() => lookup(term), 200);
      });

      field.addEventListener('focus', () => {
        const term = field.value.trim();
        if (term.length < 2) {
          showRecent();
          return;
        }
        if (results.innerHTML.trim()) {
          results.removeAttribute('hidden');
          field.setAttribute('aria-expanded', 'true');
          indexOptions();
        }
      });

      if (clear) {
        clear.addEventListener('click', () => {
          field.value = '';
          clear.setAttribute('hidden', '');
          lastTerm = '';
          hasResults = false;
          field.focus();
          showRecent();
        });
      }

      /* Wissen van de geschiedenis gebeurt binnen het paneel, dus met delegatie:
         de knop wordt bij elke render opnieuw aangemaakt. */
      results.addEventListener('click', (event) => {
        const target = event.target;
        if (target instanceof Element && target.closest('[data-nfsh-forget]')) {
          event.preventDefault();
          forgetSearches();
          hide();
          field.focus();
        }
      });

      /* Muis en toetsenbord houden dezelfde actieve rij vast. */
      results.addEventListener('mousemove', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const option = target.closest('[role="option"]');
        if (!option) return;
        const index = options.indexOf(option);
        if (index > -1 && index !== activeIndex) setActive(index);
      });

      field.addEventListener('keydown', (event) => {
        const open = !results.hasAttribute('hidden');

        if (event.key === 'Escape') {
          if (open) {
            event.preventDefault();
            hide();
          }
          return;
        }

        if (event.key === 'Tab') {
          if (open) hide();
          return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          if (!open) {
            if (field.value.trim().length < 2) showRecent();
            return;
          }
          if (!options.length) return;
          event.preventDefault();

          if (event.key === 'ArrowDown') {
            setActive(activeIndex + 1 >= options.length ? 0 : activeIndex + 1);
          } else {
            setActive(activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
          }
          return;
        }

        if (event.key === 'Home' && open && options.length) {
          event.preventDefault();
          setActive(0);
          return;
        }

        if (event.key === 'End' && open && options.length) {
          event.preventDefault();
          setActive(options.length - 1);
          return;
        }

        if (event.key === 'Enter') {
          /* Niets gekozen: het formulier verstuurt zelf, precies zoals zonder
             JavaScript. Wel eerst de zoekterm onthouden. */
          if (!open || activeIndex < 0) {
            rememberSearch(field.value);
            return;
          }
          event.preventDefault();
          rememberSearch(field.value);
          const option = options[activeIndex];
          const href = option.getAttribute('href');
          if (href) window.location.assign(href);
        }
      });

      form.addEventListener('submit', () => {
        rememberSearch(field.value);
      });

      document.addEventListener('click', (event) => {
        const target = event.target;
        if (target instanceof Node && !wrap.contains(target)) hide();
      });
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-nfsh]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', boot);
})();
